import {Dialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {usePatchDeliveryNoteItemMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {WAREHOUSE_GRID_CODES} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ControlledRowControl, type RowV5ResponseBody} from 'features/eag-grid';

import {DeliveryNoteItemDetail} from './DeliveryNoteItemDetail';

export type DeliveryNoteItemEditProps = {
  deliveryNoteId: string;
  deliveryNoteItemId: string;
  dataQueryId: string;
  isReadOnly?: boolean;
  onClose: VoidFunction;
} & RequiredTestIdProps;

export function DeliveryNoteItemEdit(props: DeliveryNoteItemEditProps) {
  const [currentItemId, setCurrentItemId] = useState(props.deliveryNoteItemId);

  const [, patchStatus] = usePatchDeliveryNoteItemMutation({
    fixedCacheKey: 'edit-delivery-note-item',
  });

  const isSavedByRowControlRef = useRef<boolean>(false);

  const formRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const isSavingChanges = patchStatus.isLoading;

  const handlePageChange = async (row: RowV5ResponseBody) => {
    const isDirty = formRef.current?.isDirty;
    const submitForm = formRef.current?.submit;

    if (!isDirty) {
      setCurrentItemId(row.id);
      return true;
    }

    isSavedByRowControlRef.current = true;

    const isSubmitSuccessful = await submitForm?.();

    if (!isSubmitSuccessful || patchStatus.isError) {
      isSavedByRowControlRef.current = false;
      return false;
    }

    setCurrentItemId(row.id);
    return true;
  };

  return (
    <Dialog
      title={i18n.t('general.labels.edit')}
      isOpen={isNotNil(props.deliveryNoteItemId)}
      size="large"
      withAdditionalFooter
      onClose={props.onClose}
      pagination={
        <Show when={isNotNil(props.dataQueryId)}>
          <ControlledRowControl
            isDisabled={isSavingChanges}
            dataQueryId={props.dataQueryId}
            gridCode={WAREHOUSE_GRID_CODES.deliveryNoteItems}
            entityId={currentItemId}
            onPageChange={handlePageChange}
          />
        </Show>
      }
      data-testid={suffixTestId('dialogs.editItem', props)}
    >
      <DeliveryNoteItemDetail
        key={currentItemId}
        deliveryNoteId={props.deliveryNoteId}
        deliveryNoteItemId={currentItemId}
        isReadOnly={props.isReadOnly}
        onClose={props.onClose}
        isSavedByRowControlRef={isSavedByRowControlRef}
        formRef={formRef}
        data-testid={suffixTestId('forms.editItem', props)}
      />
    </Dialog>
  );
}
